'use client';

import { faLoader, faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, ModalClose, ModalContent, ModalDescription, ModalHeading } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import parse from 'html-react-parser';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useSession } from '@/lib/SessionProvider';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { EventDetailsDrawer } from '@/tournament/components/events/EventDetailsDrawer';
import { EventPlayer, EventPlayerRow } from '@/tournament/components/events/EventPlayer';
import { EventStatus, getEventStatusClass } from '@/tournament/components/players/PlayerRow';
import { proBracketLevelIDs } from '@/utils/helpers/constants';
import { getBracketsAppRedirectURL } from '@/utils/helpers/getBracketsAppRedirectURL';

export interface TournamentEvent {
	showDraws: boolean;
	bracketLevelId: number;
	isCanceled: boolean;
	eventId: string;
	activitySplitId: string;
	activityId: string;
	numOfRegistered: number;
	numOfWaitlist: number;
	numOfLottery: number;
	lotteryActive: boolean;
	waitlistActive: boolean;
	playerGroup: string;
	format: string;
	skillGroup: string;
	ageGroup: string;
	rrScoreFormat?: string;
	goldScoreFormat?: string;
	silverScoreFormat?: string;
	bronzeScoreFormat?: string;
	date: string;
	title: string;
	subtitle: string;
	bracketType: string;
	maxNumOfTeams: number;
	status: EventStatus;
	multipleDates: { level: string; value: string }[];
	goldMedalTeam: string;
	silverMedalTeam: string;
	bronzeMedalTeam: string;
	players: EventPlayer[];
	bracketFormatId: number;
	hidePlayers: boolean;
	currentSequence: number;
	currentSequencePoolId: string;
	totalPools: number;
}

enum ShowPlayersType {
	ALL = 'all',
	REGISTERED = 'registered',
	WAITLIST = 'waitlist',
	LOTTERY = 'lottery'
}

interface EventProps {
	data: TournamentEvent;
	needAPartnerOnly?: boolean;
	tourneyId: string;
	isManager: boolean;
	setBracketModalOpen: Dispatch<SetStateAction<boolean>>;
	tourneyPlayersHidden: boolean;
}

export const Event = ({ data, needAPartnerOnly, tourneyId, isManager, setBracketModalOpen, tourneyPlayersHidden }: EventProps) => {
	const isCompleted = data?.status.id === 3;
	const hasMedals = data.goldMedalTeam || data.silverMedalTeam || data.bronzeMedalTeam;

	const [isLoading, setIsLoading] = useState(true);
	const [datesModal, setDatesModal] = useState(false);

	const [showEventDetails, setShowEventDetails] = useState(false);
	const [eventPlayers, setEventPlayers] = useState<EventPlayer[]>(data.players);

	const [showPlayers, setShowPlayers] = useState(needAPartnerOnly);
	const [showPlayersType, setShowPlayersType] = useState<string>();

	const getEventPlayers = async () => {
		if (!isLoading) setIsLoading(true);
		if (needAPartnerOnly) {
			setEventPlayers(data.players);
			return;
		}

		const response = await fetch(`/tournaments/api/eventPlayers?activityId=${data.activityId}&activitySplitId=${data.activitySplitId}`);

		const playersRes = (await response.json()) as EventPlayer[];

		switch (showPlayersType) {
			case ShowPlayersType.ALL:
				setEventPlayers(playersRes);
				break;
			case ShowPlayersType.REGISTERED:
				setEventPlayers(playersRes.filter((player) => player.isRegistered));
				break;
			case ShowPlayersType.WAITLIST:
				setEventPlayers(playersRes.filter((player) => player.isOnWaitlist));
				break;
			case ShowPlayersType.LOTTERY:
				setEventPlayers(playersRes.filter((player) => player.isLottery));
				break;
		}

		setIsLoading(false);
	};

	useEffect(() => {
		if (showPlayers) getEventPlayers();
	}, [showPlayers, showPlayersType]);

	useEffect(() => {
		if (needAPartnerOnly) setShowPlayers(true);
	}, [data.players]);

	useEffect(() => {
		if (!needAPartnerOnly) setShowPlayers(false);
		if (needAPartnerOnly) setIsLoading(false);
	}, [needAPartnerOnly]);

	const { session } = useSession();

	const { hasPermission } = useAuth();

	const isSuperAdmin = hasPermission(Permission.PICKLEBALL_BRACKETS_ADMIN);

	const [eventBracketRedirectURL, setEventBracketRedirectURL] = useState<string>('');

	const canShowPlayers = isSuperAdmin || isManager || (!data.hidePlayers && !tourneyPlayersHidden);

	useEffect(() => {
		if (proBracketLevelIDs.includes(data.bracketLevelId) || (session?.user && session?.user?.uuid)) {
			const url = getBracketsAppRedirectURL(
				tourneyId,
				data.activityId,
				data?.status.id,
				data.currentSequence,
				data.bracketFormatId,
				data.currentSequencePoolId,
				data.totalPools
			);

			setEventBracketRedirectURL(url);
		} else {
			const url = new URL(`${process.env.NEXT_PUBLIC_SSO_ENDPOINT}/`);
			url.searchParams.set('flow', 'SelfService');
			url.searchParams.set('continue', window.location.href);
			setEventBracketRedirectURL(url.toString());
		}
	}, []);
	return (
		<>
			<div>
				<div
					className={clsx(
						'flex flex-col gap-2 rounded-t-lg bg-blue-100 p-3',
						(data?.status.id !== 1 ||
							data.isCanceled ||
							(isCompleted && !hasMedals) ||
							(data.numOfLottery === 0 && data.numOfRegistered === 0 && data.numOfWaitlist === 0)) &&
							'rounded-b-lg'
					)}
				>
					<div className="flex flex-col-reverse gap-2 md:flex-col">
						<div className="flex w-full flex-row items-center gap-2 text-gray-600">
							<div className="flex w-full flex-row items-center justify-between sm:justify-normal sm:gap-4 lg:gap-6">
								<div className="flex flex-row items-center gap-2">
									<div className="text-sm lg:text-base">{data.date}</div>
									<div>
										{data.multipleDates.length > 2 && (
											<Button className="text-sm font-normal underline" onClick={() => setDatesModal(true)} variant="link-gray">
												Additional Dates
											</Button>
										)}
									</div>
								</div>
								{data?.status.id !== 1 && (
									<div className={`${getEventStatusClass(data?.status.id)} min-w-[100px]`}>
										<span>&bull; </span>
										<span className="text-sm">{data?.status.text}</span>
									</div>
								)}
							</div>
							{!data.isCanceled && (
								<div className="hidden md:block">
									{data?.status.id !== 1 &&
									(proBracketLevelIDs.includes(data.bracketLevelId) || isSuperAdmin || isManager || data.showDraws) ? (
										<a href={eventBracketRedirectURL}>
											<Button size="sm" variant="secondary" className="ml-auto  w-max bg-white ">
												Draws & Results
											</Button>
										</a>
									) : !data.showDraws && [2, 3, 4, 6].includes(data?.status.id) ? (
										<Button
											onClick={() => setBracketModalOpen(true)}
											variant="secondary"
											size="sm"
											className="ml-auto  w-max bg-white "
										>
											Draws & Results
										</Button>
									) : (
										<Button
											size="sm"
											onClick={() => {
												setShowEventDetails(true);
											}}
											variant="secondary"
											className="ml-auto  w-max bg-white "
										>
											Event Details
										</Button>
									)}
								</div>
							)}
						</div>
						<div className="text-base font-bold text-gray-900">{data?.title}</div>
					</div>
					<div className="text-sm text-gray-600 ">{data.subtitle}</div>
					{data.multipleDates.length > 0 && data.multipleDates.length <= 2 && (
						<>
							<div>
								{data.multipleDates.map((date, i) => {
									return (
										<div className="text-sm" key={i}>
											<span className="font-bold">{date.level}: </span>
											{date.value}
										</div>
									);
								})}
							</div>
						</>
					)}
					<div className="flex flex-row flex-wrap gap-[6px] text-sm sm:gap-4">
						<div className="text-gray-900">{data.bracketType}</div>
						{!data.showDraws && data?.status.id === 1 && (
							<>
								<div className="block w-px bg-gray-400" />
								<div className="text-gray-900">Max Teams: {data.maxNumOfTeams === -1 ? 'Unlimited' : data.maxNumOfTeams}</div>
							</>
						)}
					</div>
					{!data.isCanceled && (
						<div className="block md:hidden">
							{data?.status.id !== 1 &&
							(proBracketLevelIDs.includes(data.bracketLevelId) || isSuperAdmin || isManager || data.showDraws) ? (
								<a href={eventBracketRedirectURL}>
									<Button size="sm" variant="secondary" className="w-full justify-center bg-white ">
										Draws & Results
									</Button>
								</a>
							) : !data.showDraws && [2, 3, 4, 6].includes(data?.status.id) ? (
								<Button
									onClick={() => setBracketModalOpen(true)}
									variant="secondary"
									size="sm"
									className="w-full justify-center bg-white "
								>
									Draws & Results
								</Button>
							) : (
								<Button
									size="sm"
									onClick={() => {
										setShowEventDetails(true);
									}}
									variant="secondary"
									className="w-full justify-center bg-white "
								>
									Event Details
								</Button>
							)}
						</div>
					)}
				</div>
				{canShowPlayers && (data?.status.id === 1 || needAPartnerOnly) && !data.isCanceled && (
					<div className="flex flex-col rounded-b-lg bg-white px-3 py-1">
						{!needAPartnerOnly && (
							<div className="flex w-full flex-col gap-2">
								<div className="flex w-full flex-row gap-2 overflow-auto md:flex lg:w-3/5">
									<FilterButtonsGroup
										data={data}
										setShowPlayers={setShowPlayers}
										showPlayers={showPlayers}
										setShowPlayersType={setShowPlayersType}
										showPlayersType={showPlayersType}
									/>
								</div>
							</div>
						)}

						{showPlayers && (
							<>
								{isLoading ? (
									<div className="flex h-48 flex-col items-center justify-center gap-4">
										<FontAwesomeIcon className="text-xl font-extralight" icon={faLoader} spin />
										<div>Loading players...</div>
									</div>
								) : (
									<div className="mt-2 overflow-auto">
										{eventPlayers.length > 0 ? (
											<table className="w-full">
												<thead className="bg-gray-100 text-left text-gray-600">
													<tr>
														<th className="px-4 py-2 text-sm font-semibold">Teams</th>
														{!proBracketLevelIDs.includes(data.bracketLevelId) && (
															<th className="px-4 py-2 text-center text-sm font-semibold">Skill</th>
														)}
														<th className="px-4 py-2 text-center text-sm font-semibold">Age</th>
														<th className="px-4 py-2 text-center text-sm font-semibold">Gender</th>
														<th className="px-4 py-2 text-sm font-semibold">City/State</th>
														<th className="px-4 py-2 text-sm font-semibold"></th>
													</tr>
												</thead>
												<tbody>
													{eventPlayers?.map((player: EventPlayer) => (
														<EventPlayerRow
															player={player}
															key={player.playerId}
															isProLevelBracket={proBracketLevelIDs.includes(data.bracketLevelId)}
														/>
													))}
												</tbody>
											</table>
										) : (
											<div className="font-medium text-gray-700">There are no players.</div>
										)}
									</div>
								)}
							</>
						)}
					</div>
				)}

				{isCompleted && hasMedals && (
					<div className="flex flex-col gap-4 rounded-b-lg bg-white p-4 text-sm font-semibold text-blue-600">
						{data.goldMedalTeam && (
							<div className="flex flex-row items-center gap-2">
								<img src={`${process.env.NEXT_PUBLIC_IMAGE_CDN}/pickleballtournaments-app/gold_medal.svg`} alt="" />
								{parse(data.goldMedalTeam)}
							</div>
						)}

						{data.silverMedalTeam && (
							<div className="flex flex-row items-center gap-2">
								<img src={`${process.env.NEXT_PUBLIC_IMAGE_CDN}/pickleballtournaments-app/silver_medal.svg`} alt="" />
								{parse(data.silverMedalTeam)}
							</div>
						)}

						{data.bronzeMedalTeam && (
							<div className="flex flex-row items-center gap-2">
								<img src={`${process.env.NEXT_PUBLIC_IMAGE_CDN}/pickleballtournaments-app/bronze_medal.svg`} alt="" />
								{parse(data.bronzeMedalTeam)}
							</div>
						)}
					</div>
				)}
			</div>
			<EventDetailsDrawer
				tourneyId={tourneyId}
				activityId={data.activityId}
				setShowEventDetails={setShowEventDetails}
				showEventDetails={showEventDetails}
			/>

			<Modal onOpenChange={() => setDatesModal(!datesModal)} open={datesModal} backdropClose={false}>
				<ModalContent className="flex w-[400px] flex-col justify-center">
					<ModalHeading className="flex w-full items-center justify-between">
						<div className="flex-1 text-xl font-semibold capitalize leading-7 text-gray-900">Additional Dates</div>
						<ModalClose className="ml-auto">
							<FontAwesomeIcon icon={faTimes} size="xl" className="text-gray-500" />
						</ModalClose>
					</ModalHeading>
					<ModalDescription className="mt-4 text-base font-normal text-gray-600">
						<div className="flex flex-col gap-2 text-sm">
							{data.multipleDates.map((date) => (
								<div key={date.level} className="flex flex-row items-center">
									<div className="w-[130px] min-w-[130px] p-2 font-bold">{date.level}</div>
									<div className="p-2">{date.value}</div>
								</div>
							))}
						</div>
					</ModalDescription>
				</ModalContent>
			</Modal>
		</>
	);
};

interface FilterButtonsGroupProps {
	data: TournamentEvent;
	setShowPlayers: Dispatch<SetStateAction<boolean | undefined>>;
	setShowPlayersType: Dispatch<SetStateAction<string | undefined>>;
	showPlayersType: string | undefined;
	showPlayers: boolean | undefined;
}

const FilterButtonsGroup = ({ data, setShowPlayers, showPlayers, showPlayersType, setShowPlayersType }: FilterButtonsGroupProps) => {
	useEffect(() => {
		if (showPlayersType) {
			setShowPlayers(true);
		} else {
			setShowPlayers(!showPlayers);
		}
	}, [showPlayersType]);
	return (
		<>
			<Button
				className={clsx('rounded-none p-2', showPlayersType === ShowPlayersType.ALL && 'border-b-2 border-blue-600 !text-blue-600')}
				onClick={() => {
					setShowPlayersType(showPlayersType !== ShowPlayersType.ALL ? ShowPlayersType.ALL : undefined);
				}}
				size="sm"
				variant="link-gray"
			>
				All
				<span className="ml-1 rounded-full bg-gray-100 px-2 py-1 text-sm font-normal">
					{data.numOfRegistered + (data.waitlistActive ? data.numOfWaitlist : 0) + (data.lotteryActive ? data.numOfLottery : 0)}
				</span>
			</Button>

			<Button
				onClick={() => {
					setShowPlayersType(showPlayersType !== ShowPlayersType.REGISTERED ? ShowPlayersType.REGISTERED : undefined);
				}}
				className={clsx('rounded-none p-2', showPlayersType === ShowPlayersType.REGISTERED && 'border-b-2 border-blue-600 !text-blue-600')}
				size="sm"
				variant="link-gray"
			>
				Registered
				<span className="ml-1 rounded-full bg-gray-100 px-2 py-1 text-sm  font-normal">{data.numOfRegistered}</span>
			</Button>

			{data.waitlistActive && (
				<Button
					onClick={() => {
						setShowPlayersType(showPlayersType !== ShowPlayersType.WAITLIST ? ShowPlayersType.WAITLIST : undefined);
					}}
					className={clsx('rounded-none p-2', showPlayersType === ShowPlayersType.WAITLIST && 'border-b-2 border-blue-600 !text-blue-600')}
					size="sm"
					variant="link-gray"
				>
					Waitlist
					<span className="ml-1 w-5 rounded-full bg-[#EAAA08] text-sm font-normal text-white">W</span>
					<span className="ml-1 rounded-full bg-gray-100 px-2 py-1 text-sm  font-normal">{data.numOfWaitlist}</span>
				</Button>
			)}

			{data.lotteryActive && (
				<Button
					onClick={() => {
						setShowPlayersType(showPlayersType !== ShowPlayersType.LOTTERY ? ShowPlayersType.LOTTERY : undefined);
					}}
					className={clsx('rounded-none p-2', showPlayersType === ShowPlayersType.LOTTERY && 'border-b-2 border-blue-600 !text-blue-600')}
					size="sm"
					variant="link-gray"
				>
					Lottery
					<span className="ml-1 w-5 rounded-full bg-blue-700 text-sm font-normal text-white">L</span>
					<span className="ml-1 rounded-full bg-gray-100 px-2 py-1 text-sm  font-normal">{data.numOfLottery}</span>
				</Button>
			)}
		</>
	);
};
